import { qrPath } from "@bt/domain";
import { QrCodeIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GarageGate } from "@/components/app/garages";
import { Button } from "@/components/ui/button";
import { signInUrl } from "@/lib/account";
import { garageUrl } from "@/lib/garage";

/**
 * /qr and /qr/<CODE>, where a scanned QR label lands (ADR 0018 › Where a label
 * points). The server sends a code to its garage before this loads; this does the same where the
 * server isn't in front of the pages (the Vite dev server), and says when a code isn't known here.
 * Bare /qr opens the scanner of the garage used last.
 */
export function QrEntry() {
  const path = qrPath(window.location.pathname);
  const code = path.kind === "canonical" ? path.code : null;
  if (!code) return <GarageGate path="/qr" />;
  return <QrCode code={code} />;
}

function QrCode({ code }: { code: string }) {
  const [unknown, setUnknown] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void fetch(`/api/qr/${code}`).then(async (response) => {
      if (cancelled) return;
      if (response.ok) {
        const { garage } = (await response.json()) as { garage: string };
        window.location.replace(garageUrl(garage, `/qr/${code}`));
      } else if (response.status === 401) window.location.assign(signInUrl(`/qr/${code}`));
      else if (response.status === 403) window.location.assign("/account/restricted");
      else setUnknown(true);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);
  return unknown ? <UnknownLabel /> : <div className="min-h-dvh bg-background" />;
}

/** A code no garage of this person has: unknown here, from another installation, or someone else's. */
function UnknownLabel() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <QrCodeIcon className="size-8 text-muted-foreground" aria-hidden />
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t("labels.unknown")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("labels.unknownBody")}</p>
        </div>
        <Button asChild variant="outline">
          <a href="/">{t("labels.toApp")}</a>
        </Button>
      </div>
    </div>
  );
}
